import React, { useState, useMemo, useEffect } from 'react';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { DownloadIcon } from '../../../components/icons/Icons';

const getFinancialYears = () => {
    const years = [];
    const fixedStartYear = 2024;
    const today = new Date();
    const currentMonth = today.getMonth(); // 0 = January, 3 = April
    const currentYear = today.getFullYear();
    const currentFinancialYearStart = currentMonth >= 3 ? currentYear : currentYear - 1;
    for (let year = fixedStartYear; year <= currentFinancialYearStart; year++) {
        years.push(`${year}-${year + 1}`);
    }
    return years.reverse();
};

const downloadAsCSV = (data, filename) => {
    if (!data || data.length === 0) return;
    const headers = Object.keys(data[0]).join(',');
    const rows = data.map(row => Object.values(row).map(value => `"${String(value).replace(/"/g, '""')}"`).join(','));
    const csvContent = `${headers}\n${rows.join('\n')}`;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

export default function ExpenseStatements({ appId }) {
    const [selectedYear, setSelectedYear] = useState(getFinancialYears()[0]);
    const [selectedMonth, setSelectedMonth] = useState('Annual');
    const [statementData, setStatementData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [userMap, setUserMap] = useState({});
    const [reportType, setReportType] = useState('summary');
    const [selectedEntity, setSelectedEntity] = useState('All');
    const [allCategories, setAllCategories] = useState([]);

    const financialYears = useMemo(() => getFinancialYears(), []);
    const months = ["Annual", "April", "May", "June", "July", "August", "September", "October", "November", "December", "January", "February", "March"];

    useEffect(() => {
        const fetchCategories = async () => {
            if (!appId) return;
            const categoriesRef = collection(db, `/artifacts/${appId}/public/data/expenseCategories`);
            const q = query(categoriesRef, orderBy('name'));
            const snapshot = await getDocs(q);
            const fetchedCategories = snapshot.docs.map(doc => doc.data().name);
            setAllCategories(fetchedCategories);
        };
        fetchCategories();
    }, [appId]);

    useEffect(() => {
        const fetchUsers = async () => {
            if (!appId) return;
            const usersRef = collection(db, `/artifacts/${appId}/users`);
            const snapshot = await getDocs(usersRef);
            const map = {};
            snapshot.forEach(doc => {
                map[doc.id] = doc.data();
            });
            setUserMap(map);
        };
        fetchUsers();
    }, [appId]);

    const handleGenerateStatement = async () => {
        setIsLoading(true);
        setHasSearched(true);
        setStatementData([]);

        const [startYear, endYear] = selectedYear.split('-').map(Number);
        let startDate, endDate;
        const monthMap = { "April": 3, "May": 4, "June": 5, "July": 6, "August": 7, "September": 8, "October": 9, "November": 10, "December": 11, "January": 0, "February": 1, "March": 2 };
        if (selectedMonth === 'Annual') {
            startDate = new Date(startYear, 3, 1);
            endDate = new Date(endYear, 2, 31, 23, 59, 59);
        } else {
            const jsMonth = monthMap[selectedMonth];
            const year = jsMonth <= 2 ? endYear : startYear;
            startDate = new Date(year, jsMonth, 1);
            endDate = new Date(year, jsMonth + 1, 0, 23, 59, 59);
        }

        const reportsRef = collection(db, `/artifacts/${appId}/public/data/expenseReports`);
        let q = query(
            reportsRef,
            where('status', '==', 'Reimbursed'),
            where('reimbursementDate', '>=', startDate),
            where('reimbursementDate', '<=', endDate)
        );
        if (selectedEntity !== 'All') {
            q = query(q, where('entity', '==', selectedEntity));
        }
        const querySnapshot = await getDocs(q);

        if (reportType === 'summary') {
            const reports = querySnapshot.docs.map(doc => doc.data());
            const aggregatedData = reports.reduce((acc, report) => {
                const { requesterId, requesterName, reimbursementCurrency, totalAmount } = report;
                if (!requesterId) return acc;
                const userProfile = userMap[requesterId];
                const employeeId = userProfile?.employeeId || 'N/A';
                if (!acc[requesterId]) {
                    acc[requesterId] = { EmployeeID: employeeId, EmployeeName: requesterName, INR: 0, SGD: 0 };
                }
                if (reimbursementCurrency === 'INR') acc[requesterId].INR += totalAmount;
                else if (reimbursementCurrency === 'SGD') acc[requesterId].SGD += totalAmount;
                return acc;
            }, {});
            const finalData = Object.values(aggregatedData).map(emp => ({ ...emp, INR: emp.INR.toFixed(2), SGD: emp.SGD.toFixed(2) })).sort((a, b) => a.EmployeeName.localeCompare(b.EmployeeName));
            setStatementData(finalData);
        } else { // Detailed Pivot Table Logic
            const itemPromises = querySnapshot.docs.map(reportDoc => {
                const itemsRef = collection(db, reportDoc.ref.path, 'expenseItems');
                return getDocs(itemsRef).then(itemSnapshot => itemSnapshot.docs.map(itemDoc => ({...itemDoc.data(), parentReport: reportDoc.data()})));
            });
            const allItemsArrays = await Promise.all(itemPromises);
            const allItems = allItemsArrays.flat();
            
            const aggregatedData = allItems.reduce((acc, item) => {
                const { requesterId, requesterName } = item.parentReport;
                if (!requesterId) return acc;
                const userProfile = userMap[requesterId];
                const employeeId = userProfile?.employeeId || 'N/A';
                
                if (!acc[requesterId]) {
                    acc[requesterId] = {
                        EmployeeID: employeeId,
                        EmployeeName: requesterName,
                        Total: 0,
                        ...allCategories.reduce((catAcc, cat) => ({...catAcc, [cat]: 0}), {})
                    };
                }
                acc[requesterId][item.category] = (acc[requesterId][item.category] || 0) + item.reimbursableAmount;
                acc[requesterId].Total += item.reimbursableAmount;
                return acc;
            }, {});

            const finalData = Object.values(aggregatedData).map(emp => {
                const formattedEmp = { ...emp };
                formattedEmp.Total = emp.Total.toFixed(2);
                allCategories.forEach(cat => {
                    formattedEmp[cat] = emp[cat].toFixed(2);
                });
                return formattedEmp;
            }).sort((a, b) => a.EmployeeName.localeCompare(b.EmployeeName));
            setStatementData(finalData);
        }
        setIsLoading(false);
    };

    const renderContent = () => {
        if (isLoading) return <LoadingSpinner />;
        if (hasSearched && statementData.length === 0) return <p className="text-center text-gray-500 py-8">No data found for the selected period.</p>;
        if (statementData.length > 0) {
            return reportType === 'summary' ? (
                <SummaryTable data={statementData} />
            ) : (
                <DetailedTable data={statementData} categories={allCategories} />
            );
        }
        return <p className="text-center text-gray-500 py-8">Select a period and report type, then click "Generate Statement".</p>;
    };

    return (
        <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-xl font-semibold text-[#213368] mb-4">Generate Expense Statement</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 items-end gap-4 mb-6 p-4 bg-gray-50 rounded-lg border">
                <div>
                    <label className="block text-sm font-medium text-gray-700">Report Type</label>
                    <div className="mt-1 flex rounded-md shadow-sm">
                        <button onClick={() => setReportType('summary')} className={`px-4 py-2 text-sm border ${reportType === 'summary' ? 'bg-[#213368] text-white border-[#213368]' : 'bg-white border-gray-300'} rounded-l-md`}>Summary</button>
                        <button onClick={() => setReportType('detailed')} className={`-ml-px px-4 py-2 text-sm border ${reportType === 'detailed' ? 'bg-[#213368] text-white border-[#213368]' : 'bg-white border-gray-300'} rounded-r-md`}>Detailed</button>
                    </div>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">Billing Entity</label>
                    <select value={selectedEntity} onChange={e => setSelectedEntity(e.target.value)} className="mt-1 p-2 w-full border border-gray-300 rounded-md shadow-sm">
                        <option>All</option>
                        <option>Singapore</option>
                        <option>India</option>
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">Financial Year</label>
                    <select value={selectedYear} onChange={e => setSelectedYear(e.target.value)} className="mt-1 p-2 w-full border border-gray-300 rounded-md shadow-sm">
                        {financialYears.map(year => <option key={year}>{year}</option>)}
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">Month</label>
                    <select value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)} className="mt-1 p-2 w-full border border-gray-300 rounded-md shadow-sm">
                        {months.map(month => <option key={month}>{month}</option>)}
                    </select>
                </div>
                <div className="lg:col-span-2 flex items-center space-x-4">
                    <button onClick={handleGenerateStatement} className="w-full px-4 py-2 bg-[#20BEC6] text-white font-semibold rounded-lg shadow-md hover:bg-opacity-90 disabled:bg-gray-400" disabled={isLoading}>
                        {isLoading ? 'Generating...' : 'Generate Statement'}
                    </button>
                    {statementData.length > 0 && !isLoading && (
                        <button onClick={() => downloadAsCSV(statementData, `Expense-Statement-${selectedYear}-${selectedMonth}-${reportType}.csv`)} className="flex items-center w-full px-4 py-2 bg-blue-600 text-white font-semibold rounded-lg shadow-md hover:bg-blue-700">
                            <DownloadIcon className="h-5 w-5 mr-2" /> Download CSV
                        </button>
                    )}
                </div>
            </div>
            {renderContent()}
        </div>
    );
}

const SummaryTable = ({ data }) => (
    <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-100">
                <tr>
                    <th className="px-6 py-3 text-left text-sm font-bold text-gray-600 uppercase">Employee ID</th>
                    <th className="px-6 py-3 text-left text-sm font-bold text-gray-600 uppercase">Employee Name</th>
                    <th className="px-6 py-3 text-right text-sm font-bold text-gray-600 uppercase">Total Reimbursed (INR)</th>
                    <th className="px-6 py-3 text-right text-sm font-bold text-gray-600 uppercase">Total Reimbursed (SGD)</th>
                </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
                {data.map(row => (
                    <tr key={row.EmployeeID || row.EmployeeName}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-500">{row.EmployeeID}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-800">{row.EmployeeName}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-600">₹{row.INR}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-600">S${row.SGD}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);

const DetailedTable = ({ data, categories }) => (
    <div className="overflow-x-auto border rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-100">
                <tr>
                    <th className="px-4 py-3 text-left text-sm font-bold text-gray-600 uppercase sticky left-0 bg-gray-100 z-10">Employee ID</th>
                    <th className="px-4 py-3 text-left text-sm font-bold text-gray-600 uppercase sticky left-24 bg-gray-100 z-10">Employee Name</th>
                    {categories.map(cat => (
                        <th key={cat} className="px-4 py-3 text-right text-sm font-bold text-gray-600 uppercase">{cat}</th>
                    ))}
                    <th className="px-4 py-3 text-right text-sm font-bold text-gray-600 uppercase sticky right-0 bg-gray-100 z-10">Total</th>
                </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
                {data.map(row => (
                    <tr key={row.EmployeeID || row.EmployeeName}>
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-mono text-gray-500 sticky left-0 bg-white z-10">{row.EmployeeID}</td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-800 sticky left-24 bg-white z-10">{row.EmployeeName}</td>
                        {categories.map(cat => (
                            <td key={cat} className="px-4 py-4 whitespace-nowrap text-sm text-right text-gray-600">{row[cat]}</td>
                        ))}
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-right font-semibold text-gray-800 sticky right-0 bg-white z-10">{row.Total}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);